import * as React from 'react'

import { RedGreenText } from '../../components/RedGreenText'
import { TableAlign } from '../../components/Table'
import { SortableColumn } from '../../hooks/use-sort-data'
import { formatNumber, shortFormatNumber } from '../../util'
import { TickerButton } from './components/TickerButton'
import {
  HoldingColumnEnum,
  HoldingOptionTypeLabelMap,
  HoldingTransactionType,
  HoldingTransactionTypeLabelMap,
  ManagerHolding,
} from './constants'

interface ManagerHoldingsColumnType extends SortableColumn<HoldingColumnEnum, ManagerHolding> {
  name: string
  align?: TableAlign
  className?: string
  getTitle?: (data: ManagerHolding) => string
  getColumnClassName?: (data: ManagerHolding) => string | null | undefined
  renderColumn: (data: ManagerHolding) => React.ReactNode
}

const columns: ManagerHoldingsColumnType[] = [
  {
    id: HoldingColumnEnum.ticker,
    name: 'Ticker',
    align: TableAlign.Left,
    className: 'min-w-20',
    renderColumn: (data) => (
      <TickerButton
        className="font-normal"
        ticker={data.issuerTicker}
        name={data.issuerName}
        boxoverData={data.boxoverData}
      />
    ),
  },
  {
    id: HoldingColumnEnum.name,
    name: 'Name',
    className: 'min-w-60 truncate',
    align: TableAlign.Left,
    getTitle: (data) => data.issuerName,
    renderColumn: (data) => data.issuerName,
  },
  {
    id: HoldingColumnEnum.type,
    name: 'Type',
    className: 'min-w-12',
    align: TableAlign.Left,
    renderColumn: (data) => HoldingOptionTypeLabelMap[data.putCall],
  },
  {
    id: HoldingColumnEnum.sector,
    name: 'Sector',
    className: 'min-w-40 max-w-40 3xl:min-w-56 3xl:max-w-56 truncate',
    align: TableAlign.Left,
    getTitle: (data) => data.sector,
    renderColumn: (data) => data.sector || '-',
  },
  {
    id: HoldingColumnEnum.sharesHeld,
    name: 'Shares Held',
    className: 'min-w-24',
    align: TableAlign.Right,
    renderColumn: (data) => formatNumber(data.shares, { fractions: 0 }),
  },
  {
    id: HoldingColumnEnum.marketValue,
    name: 'Market Value',
    className: 'min-w-24',
    align: TableAlign.Right,
    renderColumn: (data) => shortFormatNumber(data.valueUsd),
  },
  {
    id: HoldingColumnEnum.portfolioPercentage,
    name: '% P.',
    className: 'min-w-16',
    align: TableAlign.Right,
    renderColumn: (data) => (data.subAdvisorFlag ? 'Subadvisor' : `${shortFormatNumber(data.percentageOfPortfolio)}%`),
  },
  {
    id: HoldingColumnEnum.previousPortfolioPercentage,
    name: 'Prev. % P.',
    className: 'min-w-20',
    align: TableAlign.Right,
    renderColumn: (data) => `${shortFormatNumber(data.previousPercentageOfPortfolio)}%`,
  },
  {
    id: HoldingColumnEnum.changeInShares,
    name: 'Chng. in Shares',
    className: 'min-w-28',
    align: TableAlign.Right,
    renderColumn: (data) => {
      const value = data.changeInShares

      if (!value) return 'No Change'

      return (
        <RedGreenText value={value} rightContent={null}>
          {shortFormatNumber(value, { defaultValue: 'No Change' })}
        </RedGreenText>
      )
    },
  },
  {
    id: HoldingColumnEnum.changeInSharesPercentage,
    name: '% Change',
    className: 'min-w-20',
    align: TableAlign.Right,
    renderColumn: (data) => {
      const value = data.percentChangeFromPrevious

      if (!value) return HoldingTransactionTypeLabelMap[HoldingTransactionType.NoChange]
      if (value === 100) return HoldingTransactionTypeLabelMap[HoldingTransactionType.New]
      if (value === -100) return HoldingTransactionTypeLabelMap[HoldingTransactionType.ClosedOut]

      return (
        <RedGreenText value={value} rightContent={null}>
          {shortFormatNumber(value, { defaultValue: '' })}%
        </RedGreenText>
      )
    },
  },
  {
    id: HoldingColumnEnum.ownershipPercentage,
    name: '% Ownership',
    className: 'min-w-24',
    align: TableAlign.Right,
    renderColumn: (data) => {
      const value = data.percOwnership
      return value ? `${shortFormatNumber(value)}%` : '-'
    },
  },
  {
    id: HoldingColumnEnum.averagePrice,
    name: 'Avg. Price',
    className: 'min-w-22',
    align: TableAlign.Right,
    renderColumn: (data) => shortFormatNumber(data.weightedAvgPrice),
  },
  {
    id: HoldingColumnEnum.currentPrice,
    name: 'Current Price',
    className: 'min-w-24',
    align: TableAlign.Right,
    renderColumn: (data) => shortFormatNumber(data.lastClose),
  },
  {
    id: HoldingColumnEnum.quarterFirstOwned,
    name: 'Q 1st Owned',
    className: 'min-w-24',
    align: TableAlign.Right,
    renderColumn: (data) => {
      const value = data.firstQtrOwned
      return `${value.slice(-2)}'${value.slice(2, 4)}`
    },
  },
]

export const MANAGER_HOLDINGS_TABLE_COLUMNS = Object.freeze(columns)
